import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ROUTE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/catalogue': 'Catalogue',
  '/orders': 'Orders',
  '/analytics': 'Analytics',
  '/reviews': 'Reviews',
  '/messages': 'Messages',
  '/notifications': 'Notifications',
  '/settings': 'Settings',
};

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const pageTitle = ROUTE_TITLES[location.pathname] || 'Seller Hub';

  useEffect(() => {
    document.title = `${pageTitle} | Readora Seller`;
  }, [pageTitle]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="h-16 bg-white border-b border-cream-200 flex items-center justify-between px-4 lg:px-8 flex-shrink-0">
      {/* Left: Hamburger + Title */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="lg:hidden w-10 h-10 rounded-xl bg-cream-50 hover:bg-cream-200 flex items-center justify-center 
                     transition-colors"
          aria-label="Open menu"
        >
          <svg className="w-5 h-5 text-teal-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <h1 className="text-xl font-bold text-teal-900 tracking-tight">{pageTitle}</h1>
      </div>

      {/* Right: Bell + User */}
      <div className="flex items-center gap-3">
        {/* Notification Bell */}
        <button
          onClick={() => navigate('/notifications')}
          className="relative w-10 h-10 rounded-xl bg-cream-50 hover:bg-cream-200 flex items-center justify-center 
                     transition-colors"
          aria-label="Notifications"
        >
          <span className="text-lg">🔔</span>
          <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-coral text-white text-[10px] font-bold 
                           rounded-full flex items-center justify-center animate-pulse-soft">
            3
          </span>
        </button>

        {/* User Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-cream-50 transition-colors"
          >
            <div className="w-8 h-8 rounded-lg bg-teal flex items-center justify-center">
              <span className="text-xs font-bold text-white">
                {user?.name?.charAt(0)?.toUpperCase() || 'S'}
              </span>
            </div>
            <span className="hidden md:block text-sm font-semibold text-gray-700 max-w-[120px] truncate">
              {user?.name || 'Seller'}
            </span>
            <svg
              className={`hidden md:block w-4 h-4 text-gray-400 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showDropdown && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-cream-200 
                            py-2 z-50 animate-slide-up">
              {/* User Info */}
              <div className="px-4 py-3 border-b border-cream-100">
                <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email}</p>
                {user?.business_name && (
                  <div className="flex items-center gap-1.5 mt-2">
                    <span className="text-xs">🏪</span>
                    <span className="text-xs text-emerald font-medium truncate">{user.business_name}</span>
                  </div>
                )}
              </div>

              {/* Menu Items */}
              <button
                onClick={() => { setShowDropdown(false); navigate('/settings'); }}
                className="w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-cream-50 text-left font-medium 
                           transition-colors flex items-center gap-2.5"
              >
                <span>👤</span> Profile & Settings
              </button>
              <div className="border-t border-cream-100 mt-1 pt-1">
                <button
                  onClick={() => { setShowDropdown(false); handleLogout(); }}
                  className="w-full px-4 py-2.5 text-sm text-coral hover:bg-coral-50 text-left font-medium 
                             transition-colors flex items-center gap-2.5"
                >
                  <span>🚪</span> Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
